import * as React from 'react';

import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { Button, DialogActions, Divider, Typography, Box, Grid, Paper, FormControl, FormLabel, FormGroup, FormControlLabel, Checkbox, IconButton, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChooseMishnaForm from '../shared/ChooseMishna/ChooseMishnaForm';
import { iLink, iParallelLink, iTractate, iSubline } from '../../types/types';
import PageService from '../../services/pageService';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  onClose: (link: (iLink & { selectedSublineIndices?: number[]; currentSublineIndices?: number[]; }) | null) => void;
  currentLineSublines?: iSubline[];
  editingParallel?: iParallelLink | null; // For editing existing parallel
}

export default function LinkPopup(props: Props) {
  const { open, onClose, currentLineSublines = [], editingParallel = null } = props;
  const { i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const [makbila, setMakbila] = React.useState<iLink | null>(null);
  const [allTractates, setAllTractates] = React.useState<iTractate[]>([]);
  const [defaultValues, setDefaultValues] = React.useState<iLink>({
    tractate: '',
    chapter: '',
    mishna: '',
    lineNumber: '00001',
  });
  const [selectedLineSublines, setSelectedLineSublines] = React.useState<iSubline[]>([]);
  const [selectedSublineIndices, setSelectedSublineIndices] = React.useState<number[]>([]);
  const [currentSublineIndices, setCurrentSublineIndices] = React.useState<number[]>([]);

  React.useEffect(() => {
    PageService.getAllTractates().then((tractates) => {
      setAllTractates(tractates);
      
      // Set default values from first tractate
      if (tractates && tractates.length > 0) {
        const firstTractate = tractates[0];
        const firstChapter = firstTractate.chapters?.[0];
        const firstMishna = firstChapter?.mishnaiot?.[0];
        
        setDefaultValues({
          tractate: firstTractate.id,
          chapter: firstChapter?.id || '',
          mishna: firstMishna?.mishna || '',
          lineNumber: '00001',
        });
      }
    });
  }, []);

  // Initialize dialog state when opened
  React.useEffect(() => {
    if (!open) return;

    if (editingParallel) {
      initializeEditMode();
    } else {
      resetToAddMode();
    }
  }, [open, editingParallel]);

  const initializeEditMode = async () => {
    // Pre-populate form with existing parallel data
    setMakbila({
      tractate: editingParallel!.tractate,
      chapter: editingParallel!.chapter,
      mishna: editingParallel!.mishna,
      lineNumber: editingParallel!.lineNumber
    });
    
    setSelectedSublineIndices(editingParallel!.selectedSublineIndices || []);
    setCurrentSublineIndices(editingParallel!.currentSublineIndices || []);
    
    // Load target line sublines for editing
    try {
      const mishnaData = await PageService.getMishna(
        editingParallel!.tractate, 
        editingParallel!.chapter, 
        editingParallel!.mishna
      );
      const selectedLine = mishnaData.lines.find(l => l.lineNumber === editingParallel!.lineNumber);
      setSelectedLineSublines(selectedLine?.sublines || []);
    } catch {
      setSelectedLineSublines([]);
    }
  };

  const resetToAddMode = () => {
    setSelectedSublineIndices([]);
    setCurrentSublineIndices([]);
    setMakbila(null);
    setSelectedLineSublines([]);
  };

  const handleNavigationUpdate = async (link: iLink) => {
    setMakbila(link);
    
    // Load sublines when a complete line is selected
    if (link.tractate && link.chapter && link.mishna && link.lineNumber) {
      await loadTargetLineSublines(link);
    }
  };

  // Extracted function to load target line sublines (for refresh functionality)
  const loadTargetLineSublines = async (link: iLink) => {
    try {
      const mishnaData = await PageService.getMishna(link.tractate, link.chapter, link.mishna);
      const selectedLine = mishnaData.lines.find(l => l.lineNumber === link.lineNumber);
      setSelectedLineSublines(selectedLine?.sublines || []);
    } catch {
      setSelectedLineSublines([]);
    }
  };

  const handleRefresh = async () => {
    if (makbila && makbila.tractate && makbila.chapter && makbila.mishna && makbila.lineNumber) {
      await loadTargetLineSublines(makbila);
    }
  };

  const handleOpenInNewTab = () => {
    if (makbila && makbila.tractate && makbila.chapter && makbila.mishna && makbila.lineNumber) {
      window.open(`/admin/edit/${makbila.tractate}/${makbila.chapter}/${makbila.mishna}/${makbila.lineNumber}`);
    }
  };

  const handleClose = () => {
    if (makbila) {
      const linkWithSublines = {
        ...makbila,
        selectedSublineIndices,
        currentSublineIndices
      };
      onClose(linkWithSublines);
    } else {
      onClose(null);
    }
  };

  // Check if selection is valid (equal number of sublines selected on both sides)
  const isSelectionValid = () => {
    if (currentLineSublines.length === 0 || selectedLineSublines.length === 0) {
      return makbila !== null; // If no sublines, just need a line selected
    }
    return currentSublineIndices.length > 0 && 
           currentSublineIndices.length === selectedSublineIndices.length;
  };

  return (
    <Dialog
      sx={{ direction: 'ltr' }}
      open={open}
      maxWidth={'lg'}
      PaperProps={{
        sx: {
          padding: '1rem',
          width: '90%',
        },
      }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{editingParallel ? 'ערוך מקבילה' : 'בחר מקבילה'}</span>
        <Box>
          {makbila && makbila.tractate && makbila.chapter && makbila.mishna && makbila.lineNumber && (
            <>
              <Tooltip title="רענן נתונים">
                <IconButton 
                  onClick={handleRefresh}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="פתח בכרטיסייה חדשה">
                <IconButton 
                  onClick={handleOpenInNewTab}
                  size="small"
                >
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </DialogTitle>

      <ChooseMishnaForm
        initValues={editingParallel || defaultValues}
        allChapterAllowed={false}
        allTractates={allTractates}
        onNavigationUpdated={handleNavigationUpdate}
      />

      {/* Side-by-side subline selection */}
      {(selectedLineSublines.length > 0 || currentLineSublines.length > 0) && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
            בחר תת-שורות מתואמות
          </Typography>
          
          <Grid container spacing={3}>
            {/* Current Line Sublines */}
            {currentLineSublines.length > 0 && (
              <Grid item xs={6}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <FormControl component="fieldset" fullWidth>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>
                        השורה הנוכחית ({currentSublineIndices.length} נבחרו)
                      </FormLabel>
                      <Checkbox
                        size="small"
                        indeterminate={currentSublineIndices.length > 0 && currentSublineIndices.length < currentLineSublines.length}
                        checked={currentSublineIndices.length === currentLineSublines.length && currentLineSublines.length > 0}
                        onChange={(event) => {
                          if (event.target.checked) {
                            // Select all
                            setCurrentSublineIndices(currentLineSublines.map((_, index) => index));
                          } else {
                            // Deselect all
                            setCurrentSublineIndices([]);
                          }
                        }}
                        sx={{ 
                          '& .MuiSvgIcon-root': { fontSize: 20 },
                          ml: 1
                        }}
                      />
                    </Box>
                    <FormGroup>
                      {currentLineSublines.map((subline, index) => (
                        <FormControlLabel
                          key={index}
                          control={
                            <Checkbox 
                              checked={currentSublineIndices.includes(index)}
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setCurrentSublineIndices([...currentSublineIndices, index].sort((a, b) => a - b));
                                } else {
                                  setCurrentSublineIndices(currentSublineIndices.filter(i => i !== index));
                                }
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="subtitle2" color="primary">
                                תת-שורה {index + 1}
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                direction: 'rtl', 
                                textAlign: 'right',
                                lineHeight: 1.6,
                                maxWidth: '300px'
                              }}>
                                {subline.text}
                              </Typography>
                            </Box>
                          }
                          sx={{ 
                            alignItems: 'flex-start',
                            mb: 2,
                            '& .MuiFormControlLabel-label': {
                              width: '100%'
                            }
                          }}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Paper>
              </Grid>
            )}

            {/* Target Line Sublines */}
            {selectedLineSublines.length > 0 && (
              <Grid item xs={currentLineSublines.length > 0 ? 6 : 12}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <FormControl component="fieldset" fullWidth>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>
                        השורה שנבחרה - יעד ({selectedSublineIndices.length} נבחרו)
                      </FormLabel>
                      <Checkbox
                        size="small"
                        indeterminate={selectedSublineIndices.length > 0 && selectedSublineIndices.length < selectedLineSublines.length}
                        checked={selectedSublineIndices.length === selectedLineSublines.length && selectedLineSublines.length > 0}
                        onChange={(event) => {
                          if (event.target.checked) {
                            // Select all
                            setSelectedSublineIndices(selectedLineSublines.map((_, index) => index));
                          } else {
                            // Deselect all
                            setSelectedSublineIndices([]);
                          }
                        }}
                        sx={{ 
                          '& .MuiSvgIcon-root': { fontSize: 20 },
                          ml: 1
                        }}
                      />
                    </Box>
                    <FormGroup>
                      {selectedLineSublines.map((subline, index) => (
                        <FormControlLabel
                          key={index}
                          control={
                            <Checkbox 
                              checked={selectedSublineIndices.includes(index)}
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setSelectedSublineIndices([...selectedSublineIndices, index].sort((a, b) => a - b));
                                } else {
                                  setSelectedSublineIndices(selectedSublineIndices.filter(i => i !== index));
                                }
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="subtitle2" color="primary">
                                תת-שורה {index + 1}
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                direction: 'rtl', 
                                textAlign: 'right',
                                lineHeight: 1.6,
                                maxWidth: '300px'
                              }}>
                                {subline.text}
                              </Typography>
                            </Box>
                          }
                          sx={{ 
                            alignItems: 'flex-start',
                            mb: 2,
                            '& .MuiFormControlLabel-label': {
                              width: '100%'
                            }
                          }}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Paper>
              </Grid>
            )}
          </Grid>
        </>
      )}
      <DialogActions>
        <Button
          onClick={() => {
            onClose(null);
          }}>
          בטל
        </Button>
        
        {/* Show validation message */}
        {(currentLineSublines.length > 0 || selectedLineSublines.length > 0) && !isSelectionValid() && (
          <Typography variant="body2" color="error" sx={{ mx: 2 }}>
            יש לבחור מספר זהה של תת-שורות משני הצדדים
          </Typography>
        )}
        
        <Button 
          onClick={handleClose}
          disabled={!isSelectionValid()}
          variant={isSelectionValid() ? "contained" : "outlined"}
        >
          {editingParallel ? 'עדכן' : 'בחר'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
